import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Star, Copy, Zap, Sparkles, Search, GripVertical, Camera, Printer, FileText, ClipboardCopy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown } from "lucide-react";
import api, { formatPrice } from "@/lib/api";
import { toast } from "sonner";
import QuickAddVehicleDialog from "@/components/QuickAddVehicleDialog";
import VinScannerDialog from "@/components/VinScannerDialog";
import AiDescriptionTools from "@/components/AiDescriptionTools";
import PhotoUploader from "@/components/PhotoUploader";

const BLANK = {
  make: "",
  model: "",
  year: new Date().getFullYear(),
  price: 0,
  mileage: 0,
  body_type: "Sedan",
  transmission: "Automatic",
  fuel_type: "Gasoline",
  drivetrain: "FWD",
  exterior_color: "",
  interior_color: "",
  description: "",
  features: "",
  image_urls: [],
  status: "available",
  featured: false,
};

const STATUS_OPTIONS = ["available", "pending", "coming_soon", "sold"];

export default function AdminInventory() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [decoding, setDecoding] = useState(false);
  const dragIndex = useRef(null);
  const [scanOpen, setScanOpen] = useState(false);

  const onDropRow = async (targetIndex) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === targetIndex) return;
    const next = [...cars];
    const [moved] = next.splice(from, 1);
    next.splice(targetIndex, 0, moved);
    setCars(next);
    try {
      await api.put("/cars/reorder", { ids: next.map((c) => c.id) });
      toast.success("Display order saved");
      load();
    } catch (err) {
      toast.error("Failed to save order");
      load();
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/cars");
      setCars(r.data || []);
    } catch (e) {
      console.warn("Failed to load inventory:", e);
      setCars([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    setEditing(null);
    setForm({ ...BLANK, vin: "" });
    setOpen(true);
  };

  const startEdit = (car) => {
    setEditing(car);
    setForm({
      ...car,
      vin: car.vin || "",
      features: (car.features || []).join(", "),
      image_urls: car.image_urls || [],
    });
    setOpen(true);
  };

  const decodeVinInEdit = async (vinOverride) => {
    const v = ((typeof vinOverride === "string" ? vinOverride : form.vin) || "").trim().toUpperCase();
    if (v.length !== 17) {
      toast.error("VIN must be 17 characters");
      return;
    }
    setDecoding(true);
    try {
      const r = await api.get(`/vin/decode/${v}`);
      const d = r.data || {};
      setForm((f) => ({
        ...f,
        year: d.year || f.year,
        make: (d.make || f.make || "").toString().replace(/\b\w/g, (c) => c.toUpperCase()),
        model: d.model || f.model,
        body_type: d.body_type || f.body_type,
        transmission: d.transmission || f.transmission,
        fuel_type: d.fuel_type || f.fuel_type,
        drivetrain: d.drivetrain || f.drivetrain,
      }));
      toast.success("VIN decoded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "VIN lookup failed");
    } finally {
      setDecoding(false);
    }
  };

  const editDescFacts = () => {
    if (!form.year || !form.make || !form.model) {
      toast.error("Add year/make/model first");
      return null;
    }
    const features = typeof form.features === "string"
      ? form.features.split(",").map((s) => s.trim()).filter(Boolean)
      : (form.features || []);
    return {
      year: parseInt(form.year, 10),
      make: form.make,
      model: form.model,
      mileage: form.mileage ? parseInt(form.mileage, 10) : null,
      body_type: form.body_type || null,
      exterior_color: form.exterior_color || null,
      interior_color: form.interior_color || null,
      transmission: form.transmission || null,
      fuel_type: form.fuel_type || null,
      drivetrain: form.drivetrain || null,
      price: form.price ? parseFloat(form.price) : null,
      features,
    };
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      year: parseInt(form.year, 10) || 0,
      price: parseFloat(form.price) || 0,
      mileage: parseInt(form.mileage, 10) || 0,
      features: typeof form.features === "string"
        ? form.features.split(",").map((s) => s.trim()).filter(Boolean)
        : form.features,
      image_urls: form.image_urls || [],
    };
    try {
      if (editing) {
        await api.put(`/cars/${editing.id}`, payload);
        toast.success("Vehicle updated");
      } else {
        await api.post("/cars", payload);
        toast.success("Vehicle added to inventory");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (car) => {
    if (!window.confirm(`Delete ${car.year} ${car.make} ${car.model}?`)) return;
    try {
      await api.delete(`/cars/${car.id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const copyListing = async (car) => {
    const text = car.description?.trim()
      ? car.description
      : [
          `${car.year} ${car.make} ${car.model}`,
          car.price ? `Price: ${formatPrice(car.price)}` : null,
          car.mileage ? `${car.mileage.toLocaleString()} miles` : null,
          "\n📍 Pit Stop Motors – Nampa, ID\n📞 208-789-8284",
        ].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Listing copied — paste it into Facebook Marketplace");
    } catch (e) {
      window.prompt("Copy this listing:", text);
    }
  };

  const handleDuplicate = async (car) => {
    try {
      await api.post(`/cars/${car.id}/duplicate`);
      toast.success("Vehicle duplicated — edit the copy to update details");
      load();
    } catch (err) {
      toast.error("Failed to duplicate");
    }
  };

  return (
    <div data-testid="admin-inventory">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-3xl text-primary">Inventory</h2>
          <p className="text-sm text-muted-foreground">{cars.length} vehicle{cars.length !== 1 ? "s" : ""} in stock. Drag the grip handle to change display order.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setQuickOpen(true)} className="rounded-sm gap-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="admin-quick-add">
            <Zap className="h-4 w-4" /> Quick Add
          </Button>
          <Button onClick={startCreate} variant="outline" className="rounded-sm gap-1" data-testid="admin-add-car">
            <Plus className="h-4 w-4" /> Add Vehicle
          </Button>
        </div>
      </div>

      <div className="mt-6 border border-soft bg-card">
        <Table data-testid="admin-inventory-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Mileage</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-44 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>
            )}
            {!loading && cars.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No vehicles yet.</TableCell></TableRow>
            )}
            {cars.map((c, i) => (
              <TableRow
                key={c.id}
                data-testid={`row-car-${c.id}`}
                draggable
                onDragStart={() => { dragIndex.current = i; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onDropRow(i); }}
                className={c.status === "sold" ? "opacity-60" : ""}
              >
                <TableCell className="cursor-grab active:cursor-grabbing text-muted-foreground" data-testid={`drag-handle-${c.id}`}>
                  <GripVertical className="h-4 w-4" />
                </TableCell>
                <TableCell className="font-medium text-primary">
                  <div className="flex items-center gap-2">
                    {c.featured && <Star className="h-3 w-3 text-accent fill-accent" />}
                    {c.year} {c.make} {c.model}
                  </div>
                </TableCell>
                <TableCell>{formatPrice(c.price)}</TableCell>
                <TableCell>{c.mileage?.toLocaleString()} mi</TableCell>
                <TableCell>{c.body_type}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm uppercase text-[10px]">{c.status.replace("_", " ")}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => copyListing(c)} title="Copy listing for Facebook Marketplace" data-testid={`copy-listing-${c.id}`}>
                    <ClipboardCopy className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => window.open(`/inventory/${c.id}/sticker`, "_blank")} title="Print window sticker" data-testid={`sticker-car-${c.id}`}>
                    <Printer className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => window.open(`/inventory/${c.id}/buyers-guide`, "_blank")} title="Print FTC Buyers Guide" data-testid={`buyers-guide-car-${c.id}`}>
                    <FileText className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => startEdit(c)} title="Edit" data-testid={`edit-car-${c.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDuplicate(c)} title="Duplicate" data-testid={`duplicate-car-${c.id}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(c)} title="Delete" data-testid={`delete-car-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-car-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl text-primary">
              {editing ? "Edit Vehicle" : "Add a Vehicle"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            {/* VIN auto-fill */}
            <div className="border border-soft bg-secondary/40 p-3 rounded-sm">
              <Label htmlFor="form-vin" className="text-xs">VIN (optional — auto-fill specs)</Label>
              <div className="mt-1 flex gap-2">
                <Input
                  id="form-vin"
                  placeholder="17-character VIN"
                  value={form.vin || ""}
                  onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })}
                  className="font-mono text-sm"
                  maxLength={17}
                  data-testid="form-vin"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setScanOpen(true)}
                  className="rounded-sm gap-1"
                  title="Scan VIN with camera"
                  data-testid="form-scan-vin"
                >
                  <Camera className="h-3 w-3" /> Scan
                </Button>
                <Button
                  type="button"
                  onClick={decodeVinInEdit}
                  disabled={decoding || (form.vin || "").length !== 17}
                  className="rounded-sm gap-1 bg-accent text-accent-foreground hover:bg-accent/90"
                  data-testid="form-decode-vin"
                >
                  <Search className="h-3 w-3" /> {decoding ? "Decoding…" : "Decode"}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="make">Make *</Label>
                <Input id="make" required value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} data-testid="form-make" />
              </div>
              <div>
                <Label htmlFor="model">Model *</Label>
                <Input id="model" required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="form-model" />
              </div>
              <div>
                <Label htmlFor="year">Year *</Label>
                <Input id="year" type="number" required value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="form-year" />
              </div>
              <div>
                <Label htmlFor="price">Price ($) *</Label>
                <Input id="price" type="number" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="form-price" />
              </div>
              <div>
                <Label htmlFor="mileage">Mileage</Label>
                <Input id="mileage" type="number" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} data-testid="form-mileage" />
              </div>
              <div>
                <Label htmlFor="body">Body Type</Label>
                <Input id="body" value={form.body_type} onChange={(e) => setForm({ ...form, body_type: e.target.value })} data-testid="form-body-type" />
              </div>
              <div>
                <Label htmlFor="trans">Transmission</Label>
                <Input id="trans" value={form.transmission} onChange={(e) => setForm({ ...form, transmission: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="fuel">Fuel</Label>
                <Input id="fuel" value={form.fuel_type} onChange={(e) => setForm({ ...form, fuel_type: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="dt">Drivetrain</Label>
                <Input id="dt" value={form.drivetrain} onChange={(e) => setForm({ ...form, drivetrain: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="ext">Exterior Color</Label>
                <Input id="ext" value={form.exterior_color} onChange={(e) => setForm({ ...form, exterior_color: e.target.value })} data-testid="form-exterior-color" />
              </div>
              <div>
                <Label htmlFor="int">Interior Color</Label>
                <Input id="int" value={form.interior_color} onChange={(e) => setForm({ ...form, interior_color: e.target.value })} placeholder="Black / Gray / Tan" data-testid="form-interior-color" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="video">Video Walkaround URL (YouTube or MP4 link)</Label>
                <Input id="video" value={form.video_url || ""} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtu.be/…" data-testid="form-video-url" />
              </div>
              <div className="col-span-2">
                <Label>Status</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" className="w-full justify-between mt-1 font-normal capitalize" data-testid="form-status-trigger">
                      {form.status.replace("_", " ")} <ChevronDown className="h-3 w-3 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
                    {STATUS_OPTIONS.map((s) => (
                      <DropdownMenuItem key={s} onSelect={() => setForm({ ...form, status: s })} className="capitalize" data-testid={`form-status-option-${s}`}>
                        {s.replace("_", " ")}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label htmlFor="desc">Description</Label>
                <AiDescriptionTools
                  getFacts={editDescFacts}
                  onPick={(text) => setForm((f) => ({ ...f, description: text }))}
                  disabled={!form.make || !form.model}
                  testIdPrefix="form-ai"
                />
              </div>
              <Textarea id="desc" rows={10} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description" />
            </div>
            <div>
              <Label htmlFor="feat">Features (comma separated)</Label>
              <Input id="feat" placeholder="Sunroof, Heated Seats, Apple CarPlay" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} data-testid="form-features" />
            </div>
            <PhotoUploader
              urls={Array.isArray(form.image_urls) ? form.image_urls : []}
              onChange={(updater) => setForm((f) => ({ ...f, image_urls: updater(Array.isArray(f.image_urls) ? f.image_urls : []) }))}
              testIdPrefix="form-photo"
            />
            <div className="flex items-center gap-2">
              <Checkbox
                id="featured"
                checked={form.featured}
                onCheckedChange={(c) => setForm({ ...form, featured: !!c })}
                data-testid="form-featured"
              />
              <Label htmlFor="featured" className="cursor-pointer">Feature on homepage</Label>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="form-save">
                {editing ? "Save Changes" : "Add to Inventory"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <QuickAddVehicleDialog open={quickOpen} onOpenChange={setQuickOpen} onSaved={load} />
      <VinScannerDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        onDetected={(vin) => {
          setForm((f) => ({ ...f, vin }));
          decodeVinInEdit(vin);
        }}
      />
    </div>
  );
}
